 
 
import { axisBottom, axisLeft, drag, select } from 'd3';
import { DataPoint, SliderData, SliderType } from './types';
import { SLIDER_COLORS, GRAPH_MARGIN, PAN_END_BUFFER_FRACTION } from './constants';
import { CustomFieldParams } from '@/types/customFieldTypes';
import { UpdatedParamsAndRefs } from '@/types/parameterTypes';



export const renderDataLine = (g: any, data: DataPoint[], line: any) => {
  g.append('path')
    .datum(data)
    .attr('fill', 'none')
    .attr('stroke', '#007bff')
    .attr('stroke-width', 2)
    .attr('d', line);
};

export const countCsvDataPoints = (csvText: string): number => {
  const lines = csvText.trim().split('\n');

  if (lines.length === 1) {
    const values = lines[0].split(',').map(v => v.trim());
    return values.filter(v => {
      const num = parseFloat(v);
      return !Number.isNaN(num);
    }).length;
  }

  let count = 0;
  for (let i = 1; i < lines.length; i += 1) {
    const line = lines[i].trim();
    if (line) {
      const columns = line.split(',').map(col => col.trim());
      if (columns.length >= 2) {
        const x = parseFloat(columns[0]);
        const y = parseFloat(columns[1]);
        if (!Number.isNaN(x) && !Number.isNaN(y)) {
          count += 1;
        }
      }
    }
  }
  return count;
};

export const parseCsvData = (csvText: string, sampleRate: number = 1): DataPoint[] => {
  const dataPoints: DataPoint[] = [];
  const lines = csvText.trim().split('\n');

  if (lines.length === 1) {
    const values = lines[0].split(',').map(v => v.trim());
    values.forEach((value, index) => {
      if (index % sampleRate === 0) {
        const voltage = parseFloat(value);
        if (!Number.isNaN(voltage)) {
          dataPoints.push({ x: index, y: voltage });
        }
      }
    });
  } else {
    for (let i = 1; i < lines.length; i += 1) {
      if ((i - 1) % sampleRate === 0) {
        const line = lines[i].trim();
        if (line) {
          const columns = line.split(',').map(col => col.trim());
          if (columns.length >= 2) {
            const x = parseFloat(columns[0]);
            const y = parseFloat(columns[1]);
            if (!Number.isNaN(x) && !Number.isNaN(y)) {
              dataPoints.push({ x, y });
            }
          }
        }
      }
    }
  }

  return dataPoints;
};

export const renderLoading = (g: any, width: number, height: number) => {
  g.append('text')
    .attr('x', width / 2)
    .attr('y', height / 2)
    .attr('text-anchor', 'middle')
    .attr('fill', '#007bff')
    .style('font-size', '16px')
    .style('font-weight', 'bold')
    .text('Loading CSV data...');

  const spinner = g.append('circle')
    .attr('cx', width / 2)
    .attr('cy', height / 2 + 30)
    .attr('r', 8)
    .attr('fill', 'none')
    .attr('stroke', '#007bff')
    .attr('stroke-width', 2)
    .attr('stroke-dasharray', '20 5')
    .attr('stroke-dashoffset', 0);
  return spinner;
};

export const renderTitle = (g: any, width: number, title: string) => {
  g.append('text')
    .attr('x', width / 2)
    .attr('y', -25)
    .attr('fill', 'currentColor')
    .style('text-anchor', 'middle')
    .style('font-size', '16px')
    .style('font-weight', 'bold')
    .text(title);
};

export const renderAxes = (g: any, xScale: any, yScale: any, width: number, height: number, xAxisLabel: string, yAxisLabel: string) => {
  g.append('g')
    .attr('transform', `translate(0,${height})`)
    .call(axisBottom(xScale))
    .append('text')
    .attr('x', width / 2)
    .attr('y', 35)
    .attr('fill', 'currentColor')
    .style('text-anchor', 'middle')
    .style('font-size', '14px')
    .text(xAxisLabel);

  g.append('g')
    .call(axisLeft(yScale))
    .append('text')
    .attr('transform', 'rotate(-90)')
    .attr('y', -35)
    .attr('x', -height / 2)
    .attr('fill', 'currentColor')
    .style('text-anchor', 'middle')
    .style('font-size', '14px')
    .text(yAxisLabel);
};



export const renderNoDataState = (g: any, width: number, height: number) => {
  g.append('text')
    .attr('x', width / 2)
    .attr('y', height / 2)
    .attr('text-anchor', 'middle')
    .attr('fill', '#666')
    .style('font-size', '16px')
    .style('font-weight', 'bold')
    .text('Upload a CSV file to display data');

  g.append('text')
    .attr('x', width / 2)
    .attr('y', height / 2 + 25)
    .attr('text-anchor', 'middle')
    .attr('fill', '#999')
    .style('font-size', '12px')
    .text('Use the file parameter above to upload your data');
};

export const getSliderColor = (index: number): string => {
  const normalizedIndex = ((index % SLIDER_COLORS.length) + SLIDER_COLORS.length) % SLIDER_COLORS.length;
  return SLIDER_COLORS[normalizedIndex];
};

export const getSliderValueFromField = (field: any): number => {
  let value = 0;
  if (field) {
    const fieldValue = field.getValue();
    if (fieldValue !== undefined && fieldValue !== null) {
      const parsedValue = parseFloat(String(fieldValue));
      if (!Number.isNaN(parsedValue)) {
        value = parsedValue;
      }
    }
  }
  return value;
};


export const formatSliderValue = (value: number): string => {
  if (!Number.isFinite(value)) {
    return String(value);
  }
  const abs = Math.abs(value);
  if (value !== 0 && (abs >= 100000 || abs < 0.001)) {
    return value.toExponential(3);
  }
  return parseFloat(value.toPrecision(6)).toString();
};

export const findMaxBetweenLimits = (data: DataPoint[], limitA: number, limitB: number): DataPoint | null => {
  const lowerLimit = Math.min(limitA, limitB);
  const upperLimit = Math.max(limitA, limitB);
  let maxPoint: DataPoint | null = null;
  for (let i = 0; i < data.length; i += 1) {
    const point = data[i];
    if (point.x >= lowerLimit && point.x <= upperLimit && (maxPoint === null || point.y > maxPoint.y)) {
      maxPoint = point;
    }
  }
  return maxPoint;
};

export const renderXAxisSliders = (
  g: any,
  sliders: SliderData[],
  xScale: any,
  plotWidth: number,
  fullWidth: number,
  height: number,
  onSliderChange: (sliderId: string, value: number) => void,
  colorOffset: number = 0,
  isReadOnly: boolean = false
) => {
  sliders.forEach((slider, index) => {
    const xPosition = xScale(slider.value);
    const color = getSliderColor(index + colorOffset);

    const textStartY = height + 38;
    const lineEndY = height + 25;

    g.append('line')
      .attr('class', `x-axis-guide-${slider.id}`)
      .attr('x1', xPosition)
      .attr('y1', 0)
      .attr('x2', xPosition)
      .attr('y2', lineEndY)
      .attr('stroke', color)
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5');

    const tab = g.append('rect')
      .attr('class', `slider-tab-${slider.id}`)
      .attr('x', xPosition - 10)
      .attr('y', height + 20)
      .attr('width', 20)
      .attr('height', 16)
      .attr('fill', color)
      .attr('stroke', '#333')
      .attr('stroke-width', 1)
      .attr('rx', 3)
      .style('cursor', isReadOnly ? 'default' : 'grab');

    if (!isReadOnly) {
      const dragBehavior = drag()
        .on('start', function() {
          select(this).style('cursor', 'grabbing');
        })
        .on('drag', function(event) {
          const newX = Math.max(0, Math.min(plotWidth, event.x));

          select(this).attr('x', newX - 10);
          g.select(`.x-axis-guide-${slider.id}`)
            .attr('x1', newX)
            .attr('x2', newX);

          const labelElement = g.select(`.slider-label-${slider.id}`);
          const labelX = newX;
          labelElement.attr('x', labelX);
          labelElement.selectAll('tspan').attr('x', labelX);
          const valueElement = g.select(`.slider-value-${slider.id}`);
          valueElement.text(`${valueElement.attr('data-base')}: ${formatSliderValue(xScale.invert(newX))}`);
        })
        .on('end', function(event) {
          const newX = Math.max(0, Math.min(plotWidth, event.x));
          const newValue = xScale.invert(newX);

          onSliderChange(slider.id, newValue);
          select(this).style('cursor', 'grab');
        });

      tab.call(dragBehavior as any);
    }

    const maxTextWidth = plotWidth;
    const lineHeight = 14;

    const measureText = (text: string): number => {
      const tempText = g.append('text')
        .attr('x', xPosition)
        .attr('y', textStartY)
        .style('font-size', '12px')
        .style('font-weight', 'bold')
        .style('visibility', 'hidden')
        .text(text);

      const textNode = tempText.node();
      const measuredWidth = textNode ? textNode.getComputedTextLength() : 0;
      tempText.remove();
      return measuredWidth;
    };

    const words = slider.name.split(/\s+/);
    const lines: string[] = [];
    let currentLine = '';

    words.forEach((word) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = measureText(testLine);

      if (testWidth <= maxTextWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
        }
        if (measureText(word) > maxTextWidth) {
          lines.push(word);
          currentLine = '';
        } else {
          currentLine = word;
        }
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    const fontSize = 12;
    const labelX = xPosition;
    const labelY = textStartY + (lines.length > 1 ? 0 : fontSize);

    const textElement = g.append('text')
      .attr('class', `slider-label-${slider.id}`)
      .attr('x', labelX)
      .attr('y', labelY)
      .attr('fill', color)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('font-weight', 'bold');

    lines.forEach((line, lineIndex) => {
      const tspan = textElement.append('tspan')
        .attr('x', labelX)
        .attr('dy', lineIndex === 0 ? '0' : lineHeight)
        .text(line);

      // The value is shown inline on the label's last line so it stays clear
      // of the drag tab below the axis
      if (lineIndex === lines.length - 1) {
        tspan
          .attr('class', `slider-value-${slider.id}`)
          .attr('data-base', line)
          .text(`${line}: ${formatSliderValue(slider.value)}`);
      }
    });
  });
};

export const renderSliders = (
  g: any,
  sliders: SliderData[],
  yScale: any,
  plotWidth: number,
  fullWidth: number,
  height: number,
  onSliderChange: (sliderId: string, value: number) => void,
  isReadOnly: boolean = false
) => {
  sliders.forEach((slider, index) => {
    const yPosition = yScale(slider.value);
    const color = getSliderColor(index);

    const textStartX = plotWidth + 15;
    const lineEndX = textStartX - 5;

    g.append('line')
      .attr('class', `y-axis-guide-${slider.id}`)
      .attr('x1', -50)
      .attr('y1', yPosition)
      .attr('x2', lineEndX)
      .attr('y2', yPosition)
      .attr('stroke', color)
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5');

    const tab = g.append('rect')
      .attr('class', `slider-tab-${slider.id}`)
      .attr('x', -65)
      .attr('y', yPosition - 8)
      .attr('width', 20)
      .attr('height', 16)
      .attr('fill', color)
      .attr('stroke', '#333')
      .attr('stroke-width', 1)
      .attr('rx', 3)
      .style('cursor', isReadOnly ? 'default' : 'grab');

    if (!isReadOnly) {
      const dragBehavior = drag()
        .on('start', function() {
          select(this).style('cursor', 'grabbing');
        })
        .on('drag', function(event) {
          const newY = Math.max(0, Math.min(height, event.y));

          select(this).attr('y', newY - 8);
          g.select(`.y-axis-guide-${slider.id}`)
            .attr('y1', newY)
            .attr('y2', newY);

          const labelElement = g.select(`.slider-label-${slider.id}`);
          const tspanCount = labelElement.selectAll('tspan').size();
          const fontSize = 12;
          const lineHeight = 14;
          const totalTextHeight = tspanCount > 1 ? (tspanCount - 1) * lineHeight + fontSize : fontSize;
          const labelY = newY - (totalTextHeight / 2) + (fontSize * 0.75);
          labelElement.attr('y', labelY);
          g.select(`.slider-value-${slider.id}`)
            .text(formatSliderValue(yScale.invert(newY)));
        })
        .on('end', function(event) {
          const newY = Math.max(0, Math.min(height, event.y));
          const newValue = yScale.invert(newY);

          onSliderChange(slider.id, newValue);
          select(this).style('cursor', 'grab');
        });

      tab.call(dragBehavior as any);
    }

    const maxTextWidth = (fullWidth + GRAPH_MARGIN.right) - textStartX;
    const lineHeight = 14;

    const measureText = (text: string): number => {
      const tempText = g.append('text')
        .attr('x', textStartX)
        .attr('y', yPosition)
        .style('font-size', '12px')
        .style('font-weight', 'bold')
        .style('visibility', 'hidden')
        .text(text);

      const textNode = tempText.node();
      const measuredWidth = textNode ? textNode.getComputedTextLength() : 0;
      tempText.remove();
      return measuredWidth;
    };

    const words = slider.name.split(/\s+/);
    const lines: string[] = [];
    let currentLine = '';

    words.forEach((word) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = measureText(testLine);

      if (testWidth <= maxTextWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
        }
        if (measureText(word) > maxTextWidth) {
          lines.push(word);
          currentLine = '';
        } else {
          currentLine = word;
        }
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    const fontSize = 12;
    const totalLineCount = lines.length + 1;
    const totalTextHeight = (totalLineCount - 1) * lineHeight + fontSize;
    const labelY = yPosition - (totalTextHeight / 2) + (fontSize * 0.75);

    const textElement = g.append('text')
      .attr('class', `slider-label-${slider.id}`)
      .attr('x', textStartX)
      .attr('y', labelY)
      .attr('fill', color)
      .attr('text-anchor', 'start')
      .style('font-size', '12px')
      .style('font-weight', 'bold');

    lines.forEach((line, lineIndex) => {
      textElement.append('tspan')
        .attr('x', textStartX)
        .attr('dy', lineIndex === 0 ? '0' : lineHeight)
        .text(line);
    });

    textElement.append('tspan')
      .attr('class', `slider-value-${slider.id}`)
      .attr('x', textStartX)
      .attr('dy', lineHeight)
      .text(formatSliderValue(slider.value));
  });
};

export const renderLoadingState = (g: any, width: number, height: number) => {
  const spinner = renderLoading(g, width, height);

  const animateSpinner = () => {
    spinner.transition()
      .duration(1000)
      .attrTween('stroke-dashoffset', () => {
        return (t: number) => `${t * 25}`;
      })
      .on('end', animateSpinner);
  };
  animateSpinner();
};

export const updateParameterValue = async (
  slider: SliderData,
  value: number,
  updateCustomField: (request: CustomFieldParams) => Promise<UpdatedParamsAndRefs>
) => {
  const field = slider.parameter.fields[0];
  if (field) {
    await updateCustomField({
      ...field,
      customValue: value.toString()
    });
  }
};

export interface BaseDomains {
  xBaseMin: number;
  xBaseMax: number;
  xBaseRange: number;
  yBaseMin: number;
  yBaseMax: number;
  yBaseRange: number;
}

export const calculateBaseDomains = (data: DataPoint[], sliders: SliderData[]): BaseDomains => {
  let dataMinX = 0;
  let dataMaxX = 1;
  if (data.length > 0) {
    dataMinX = data[0].x;
    dataMaxX = data[0].x;
    for (let i = 1; i < data.length; i += 1) {
      if (data[i].x < dataMinX) dataMinX = data[i].x;
      if (data[i].x > dataMaxX) dataMaxX = data[i].x;
    }
  }

  const xSliders = sliders.filter(s => s.type === SliderType.X);
  let sliderMinX = dataMinX;
  let sliderMaxX = dataMaxX;
  if (xSliders.length > 0) {
    sliderMinX = xSliders[0].value;
    sliderMaxX = xSliders[0].value;
    for (let i = 1; i < xSliders.length; i += 1) {
      if (xSliders[i].value < sliderMinX) sliderMinX = xSliders[i].value;
      if (xSliders[i].value > sliderMaxX) sliderMaxX = xSliders[i].value;
    }
  }
  const xBaseMin = Math.min(dataMinX, sliderMinX);
  const xBaseMax = Math.max(dataMaxX, sliderMaxX);
  const xBaseRange = xBaseMax - xBaseMin;

  let dataMaxY = 0;
  let dataMinY = 0;
  if (data.length > 0) {
    dataMaxY = data[0].y;
    dataMinY = data[0].y;
    for (let i = 1; i < data.length; i += 1) {
      if (data[i].y > dataMaxY) dataMaxY = data[i].y;
      if (data[i].y < dataMinY) dataMinY = data[i].y;
    }
  }

  const ySliders = sliders.filter(s => s.type === SliderType.Y);
  let sliderMaxY = 0;
  let sliderMinY = 0;
  if (ySliders.length > 0) {
    sliderMaxY = ySliders[0].value;
    sliderMinY = ySliders[0].value;
    for (let i = 1; i < ySliders.length; i += 1) {
      if (ySliders[i].value > sliderMaxY) sliderMaxY = ySliders[i].value;
      if (ySliders[i].value < sliderMinY) sliderMinY = ySliders[i].value;
    }
  }
  const yBaseMax = Math.max(dataMaxY, sliderMaxY, 1);
  const yBaseMin = Math.min(dataMinY, sliderMinY, 0);
  const yBaseRange = yBaseMax - yBaseMin;

  return {
    xBaseMin,
    xBaseMax,
    xBaseRange,
    yBaseMin,
    yBaseMax,
    yBaseRange
  };
};

export interface TransformedDomain {
  min: number;
  max: number;
}

export interface DataExtent {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

export const getDataExtent = (data: DataPoint[]): DataExtent => {
  let xMin = 0;
  let xMax = 1;
  let yMin = 0;
  let yMax = 1;
  if (data.length > 0) {
    xMin = data[0].x;
    xMax = data[0].x;
    yMin = data[0].y;
    yMax = data[0].y;
    for (let i = 1; i < data.length; i += 1) {
      if (data[i].x < xMin) xMin = data[i].x;
      if (data[i].x > xMax) xMax = data[i].x;
      if (data[i].y < yMin) yMin = data[i].y;
      if (data[i].y > yMax) yMax = data[i].y;
    }
  }
  return { xMin, xMax, yMin, yMax };
};

export const applyZoomAndPan = (
  baseMin: number,
  baseMax: number,
  baseRange: number,
  zoom: number,
  pan: number,
  minBound?: number,
  invertPan?: boolean,
  dataMin?: number,
  dataMax?: number
): TransformedDomain => {
  const zoomedRange = baseRange / zoom;
  let domainMin: number;
  let domainMax: number;

  if (dataMin !== undefined && dataMax !== undefined) {
    const rawPan = invertPan ? -pan : pan;
    const effectivePan = Math.max(-1, Math.min(1, rawPan));
    const buffer = zoomedRange * PAN_END_BUFFER_FRACTION;
    const panRange = Math.max(0, dataMax - dataMin - zoomedRange + 2 * buffer);
    domainMin = dataMin - buffer + ((1 + effectivePan) / 2) * panRange;
    domainMax = domainMin + zoomedRange;
  } else {
    const zoomedCenter = (baseMin + baseMax) / 2;
    const panOffset = (invertPan ? -pan : pan) * zoomedRange;
    domainMin = zoomedCenter - zoomedRange / 2 + panOffset;
    domainMax = zoomedCenter + zoomedRange / 2 + panOffset;
  }

  const min = minBound !== undefined ? Math.max(minBound, domainMin) : domainMin;

  return { min, max: domainMax };
};

export const filterDataToDomain = (
  data: DataPoint[],
  xDomain: TransformedDomain,
  yDomain: TransformedDomain
): DataPoint[] => {
  return data.filter(point => {
    const inXDomain = point.x >= xDomain.min && point.x <= xDomain.max;
    const inYDomain = point.y >= yDomain.min && point.y <= yDomain.max;
    return inXDomain && inYDomain;
  });
};

export const downsampleToMaxPoints = (data: DataPoint[], maxPoints: number): DataPoint[] => {
  if (data.length <= maxPoints) {
    return data;
  }

  const result: DataPoint[] = [];
  const step = data.length / maxPoints;

  // Always include first point
  result.push(data[0]);

  // Sample uniformly, preserving last point
  for (let i = 1; i < maxPoints - 1; i += 1) {
    const index = Math.round(i * step);
    if (index < data.length) {
      result.push(data[index]);
    }
  }

  // Always include last point if it's different from the one we just added
  const lastIndex = data.length - 1;
  if (result[result.length - 1] !== data[lastIndex]) {
    result.push(data[lastIndex]);
  }

  return result;
};
